import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchChapter } from '../store/bibleSlice';
import { fetchUserNotesByChapter, addNote, updateNote, deleteNote, fetchNoteById } from '../store/notesSlice';
import NoteModal from './NoteModal';
import { koreanBookNames } from './constants/koreanBookNames';
import { krvBookNames } from './constants/krvBookNames';
import { navigationBooks, krvBooks } from "./constants/navigationBooks";
import { books } from './constants/books';
import { fetchHighlightsByChapter, fetchHighlightsByVerse, addHighlight, addHighlightSegment, deleteHighlight } from '../store/highlightSlice';
import HighlightModal from './HighlightModal'; // Import HighlightModal
import "./VerseHighlighter.css"
import 'react-quill/dist/quill.snow.css';
import SermonEditor from './SermonEditor';
import { fetchSermonsByChapter, fetchSermonById } from '../store/sermonSlice'
import { navigateChapter } from '../utils/navigationHelpers';
import BookmarkManager from './BookmarkManager';
import { useParams } from 'react-router-dom';
import { fetchUserSettings, updateUserSettings } from '../store/settingsSlice';
import "./BibleDisplay.css"
import CustomAlert from "../utils/CustomAlert";
import { makeHighlightChapterKey } from '../utils/annotationKeys';
import InteractiveVerse from './bible/InteractiveVerse';
import { ReactComponent as NotesIcon } from '../assets/notes.svg'
import { ReactComponent as HighlighterIcon } from '../assets/highlighter.svg';
import { ReactComponent as DeleteHighlightIcon } from '../assets/delete-highlight.svg';
import { ReactComponent as CopyIcon } from '../assets/copy.svg';
import { useTranslation } from "react-i18next";

const MAX_VERSIONS = 3;
const isKoreanVersion = (version) => ['KRV', 'SAE'].includes(version);
const versionOptions = [
  { value: 'ESV', label: 'ESV' },
  { value: 'NIV', label: 'NIV' },
  { value: 'NLT', label: 'NLT' },
  { value: 'KRV', label: '개역한글' },
  { value: 'SAE', label: '새번역' },
];

const DEFAULT_HIGHLIGHT_COLOR_OPTIONS = [
  { label: 'Yellow', value: '#fef08a' },
  { label: 'Mint', value: '#bbf7d0' },
  { label: 'Lavender', value: '#e9d5ff' },
];

const BibleDisplay = () => {
  const dispatch = useDispatch();
  const userSettings = useSelector((state) => state.settings);
  const {
    highlightColor,
    highlightColors,
    defaultVersion: userDefaultVersion,
    showInterlinear: savedShowInterlinear,
    loading,
  } = userSettings;
  const menuRef = useRef(null);
  const versionMenuRef = useRef(null);
  const versionButtonRef = useRef(null);
  const userDataFetchSignatureRef = useRef('');
  const initialLoadSignatureRef = useRef('');
  const { version: paramVersion, book: paramBook, chapter: paramChapter } = useParams();

  const defaultVersion = userDefaultVersion || 'KRV';
  const defaultBook = isKoreanVersion(defaultVersion) ? '창세기' : 'Genesis';

  // Follow the homepage's theme choice so /bible-reading picks up the same
  // neumorphic palette (Slate / Sky) via var(--outer-shadow-*).
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem('bibleMemosTheme') || 'powder'; } catch (_e) { return 'powder'; }
  });
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === 'bibleMemosTheme' && event.newValue) setTheme(event.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Use state for columns and only initialize after settings are loaded
  const [columns, setColumns] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [selectedVerseId, setSelectedVerseId] = useState(null);
  const [verseText, setVerseText] = useState('');  // Stores the entire verse text
  const [startOffset, setStartOffset] = useState(0);
  const [endOffset, setEndOffset] = useState(0);
  const [isSermonEditorOpen, setIsSermonEditorOpen] = useState(false); // state for sermon editor visibility
  const [currentSermon, setCurrentSermon] = useState(null); // For editing an existing sermon
  const [isModalOpen, setModalOpen] = useState(false);
  const [isHighlightModalOpen, setIsHighlightModalOpen] = useState(false)
  const [currentNote, setCurrentNote] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [isVersionMenuOpen, setIsVersionMenuOpen] = useState(false);
  const [showColorOptions, setShowColorOptions] = useState(false);
  const [selectedText, setSelectedText] = useState("");  // Track the highlighted text
  const [highlightsForVerse, setHighlightsForVerse] = useState([]);
  const [activeHighlightAction, setActiveHighlightAction] = useState(null);
  const [verseHighlightAction, setVerseHighlightAction] = useState(null);
  const [alertMessage, setAlertMessage] = useState("");
  const [isAlertOpen, setIsAlertOpen] = useState(false)
  const [alertAutoDismissMs, setAlertAutoDismissMs] = useState(null);
  const [mappingHighlight, setMappingHighlight] = useState(null);
  const [currentTabIndex, setCurrentTabIndex] = useState(null); // Track the current tab index
  const [lastUpdatedSource, setLastUpdatedSource] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastSelectionRect, setLastSelectionRect] = useState(null);
  const [openFootnote, setOpenFootnote] = useState(null); // {verseId, marker}
  const [showInterlinear, setShowInterlinear] = useState(false);
  const selectionSnapshotRef = useRef({
    selectedText: '',
    startOffset: 0,
    endOffset: 0,
    verseText: '',
  });
  const hasURLParams = Boolean(paramVersion && paramBook && paramChapter);
  const { t } = useTranslation();
  const highlightColorOptions = (Array.isArray(highlightColors) && highlightColors.length >= 3
    ? highlightColors
    : DEFAULT_HIGHLIGHT_COLOR_OPTIONS.map((option) => option.value)
  ).map((value, index) => ({
    label: DEFAULT_HIGHLIGHT_COLOR_OPTIONS[index]?.label || `Color ${index + 1}`,
    value,
  }));
  // const userNotes = useSelector((state) => state.notes.userNotes); // User's notes
  const user = useSelector((state) => state.session.user);

  useEffect(() => {
    if (isSermonEditorOpen) {
      setSelectedTab(columns.length);
    }
  }, [isSermonEditorOpen, columns.length]);

  useEffect(() => {
    if (user && !userSettings.isLoaded) {
      dispatch(fetchUserSettings());
    }
  }, [user, userSettings.isLoaded, dispatch]);

  useEffect(() => {
    setShowInterlinear(Boolean(savedShowInterlinear));
  }, [savedShowInterlinear]);

  const normalizeColumn = useCallback((column, index = 0) => ({
    id: column?.id ?? `column-${index}`,
    version: column?.version || defaultVersion,
    book: column?.book || defaultBook,
    chapter: Math.max(1, Number.parseInt(column?.chapter, 10) || 1),
    verses: Array.isArray(column?.verses) ? column.verses : [],
    notes: Array.isArray(column?.notes) ? column.notes : [],
    highlights: Array.isArray(column?.highlights) ? column.highlights : [],
    sermons: Array.isArray(column?.sermons) ? column.sermons : [],
    loading: Boolean(column?.loading),
    error: column?.error ?? null,
  }), [defaultBook, defaultVersion]);

  const loadSavedColumns = useCallback(() => {
    try {
      const parsedColumns = JSON.parse(localStorage.getItem("columns") || "[]");
      if (!Array.isArray(parsedColumns)) {
        return [];
      }

      return parsedColumns.map((column, index) => normalizeColumn(column, index));
    } catch (error) {
      console.error("Error parsing saved columns from localStorage:", error);
      return [];
    }
  }, [normalizeColumn]);

  const getColumnsFetchSignature = useCallback((columnsToSign) => (
    (Array.isArray(columnsToSign) ? columnsToSign : [])
      .map((column, index) => {
        const normalizedColumn = normalizeColumn(column, index);
        return `${normalizedColumn.id}:${normalizedColumn.version}:${normalizedColumn.book}:${normalizedColumn.chapter}`;
      })
      .join('|')
  ), [normalizeColumn]);

  const updateColumnsSafely = useCallback((newColumns, source) => {
    console.log(`[updateColumnsSafely] Source: ${source}, Last Updated Source: ${lastUpdatedSource}`);

    setColumns((prevColumns) => {
      const resolvedColumns = typeof newColumns === "function" ? newColumns(prevColumns) : newColumns;
      const safeColumns = Array.isArray(resolvedColumns)
        ? resolvedColumns.map((column, index) => normalizeColumn(column, index))
        : prevColumns;

      // If the source is 'url', always allow updates
      if (source === "url") {
        console.log("[updateColumnsSafely] Updating columns from URL source.");
        setLastUpdatedSource(source);
        return safeColumns;
      }

      // If the last updated source was 'url', block updates from other sources
      if (lastUpdatedSource === "url" && source !== "url") {
        console.log("[updateColumnsSafely] Skipping update (URL has higher priority).");
        return prevColumns;
      }

      // Otherwise, allow updates and set the source
      console.log(`[updateColumnsSafely] Updating columns from source: ${source}`);
      setLastUpdatedSource(source);
      return safeColumns;
    });
  }, [lastUpdatedSource, normalizeColumn]);

  // Ensure this function is defined in the same file or imported
  const saveColumnsToLocalStorage = useCallback((nextColumns) => {
    const storedColumns = nextColumns.map((col, index) => normalizeColumn(col, index));
    console.log("[Saving to LocalStorage] Columns:", storedColumns);
    localStorage.setItem("columns", JSON.stringify(storedColumns));
  }, [normalizeColumn]);

  const fetchDataForColumn = useCallback(async (columnId, version, book, chapter) => {
    const results = await Promise.allSettled([
      dispatch(fetchUserNotesByChapter({ book, chapter, version })).unwrap(),
      dispatch(fetchHighlightsByChapter({ version, book, chapter })).unwrap(),
      dispatch(fetchSermonsByChapter({ book, chapter })).unwrap(),
    ]);
    const [notesResult, highlightsResult, sermonsResult] = results;

    if (notesResult.status === 'rejected') console.error("Error fetching notes:", notesResult.reason);
    if (highlightsResult.status === 'rejected') console.error("Error fetching highlights:", highlightsResult.reason);
    if (sermonsResult.status === 'rejected') console.error("Error fetching sermons:", sermonsResult.reason);

    return {
      notes: notesResult.status === 'fulfilled' ? (notesResult.value?.notes || notesResult.value || []) : [],
      highlights: highlightsResult.status === 'fulfilled' ? (highlightsResult.value?.highlights || []) : [],
      sermons: sermonsResult.status === 'fulfilled' ? (sermonsResult.value || []) : [],
    };
  }, [dispatch]);

  const fetchAndUpdateChapter = useCallback(async (version, book, chapter, source = "No context") => {
    console.log(`[fetchAndUpdateChapter] Source: ${source}, Version: ${version}, Book: ${book}, Chapter: ${chapter}`);
    try {
      const result = await dispatch(fetchChapter({ version, book, chapter }));
      if (result.error) throw new Error(result.error.message);

      updateColumnsSafely((prevColumns) => {
        const updatedColumns = [...prevColumns];
        updatedColumns[0] = {
          ...updatedColumns[0],
          version,
          book,
          chapter,
          verses: result.payload,
          loading: false,
          error: null,
        };
        return updatedColumns;
      }, source);
    } catch (error) {
      console.error(`[${source}] Error fetching chapter:`, error);
      updateColumnsSafely((prevColumns) => {
        const updatedColumns = [...prevColumns];
        updatedColumns[0] = {
          ...updatedColumns[0],
          loading: false,
          error: "Failed to fetch chapter",
        };
        return updatedColumns;
      }, source);
    }
  }, [dispatch, updateColumnsSafely]);

  // Fetch chapter and user-specific data (notes, highlights, sermons)
  const fetchAndUpdateChapterWithUserData = useCallback(async (version, book, chapter, source = "No context") => {
    console.log(`[fetchAndUpdateChapterWithUserData] Source: ${source}, Version: ${version}, Book: ${book}, Chapter: ${chapter}`);
    try {
      const result = await dispatch(fetchChapter({ version, book, chapter }));
      if (result.error) throw new Error(result.error.message);
      userDataFetchSignatureRef.current = getColumnsFetchSignature([{
        id: 'column-0',
        version,
        book,
        chapter,
      }]);

      // Update columns with fetched chapter data
      updateColumnsSafely((prevColumns) => {
        const updatedColumns = [...prevColumns];
        updatedColumns[0] = {
          ...updatedColumns[0],
          version,
          book,
          chapter,
          verses: result.payload,
          loading: false,
          error: null,
        };
        return updatedColumns;
      }, source);

      // Fetch and merge user-specific data
      if (user) {
        console.log("[Fetching User Data] Notes, Highlights, and Sermons...");
        const userData = await fetchDataForColumn(0, version, book, chapter);
        updateColumnsSafely((prevColumns) => {
          const updatedColumns = [...prevColumns];
          updatedColumns[0] = {
            ...updatedColumns[0],
            ...userData, // Merge user data (notes, highlights, sermons)
          };
          return updatedColumns;
        }, `${source} + userData`);
        userDataFetchSignatureRef.current = getColumnsFetchSignature([{
          id: 'column-0',
          version,
          book,
          chapter,
        }]);
      }
    } catch (error) {
      console.error(`[fetchAndUpdateChapterWithUserData] Error:`, error);
      updateColumnsSafely((prevColumns) => {
        const updatedColumns = [...prevColumns];
        updatedColumns[0] = {
          ...updatedColumns[0],
          loading: false,
          error: "Failed to fetch chapter",
        };
        return updatedColumns;
      }, source);
    }
  }, [dispatch, fetchDataForColumn, getColumnsFetchSignature, updateColumnsSafely, user]);

  const fetchChapterHandler = useCallback(async (version, book, chapter, source = "No context") => {
    console.log(`[fetchChapterHandler] Source: ${source}, Version: ${version}, Book: ${book}, Chapter: ${chapter}`);
    if (user) {
      console.log("[fetchChapterHandler] User is logged in. Fetching user-specific data...");
      await fetchAndUpdateChapterWithUserData(version, book, chapter, source);
    } else {
      console.log("[fetchChapterHandler] User is not logged in. Fetching chapter data only...");
      await fetchAndUpdateChapter(version, book, chapter, source);
    }
  }, [fetchAndUpdateChapter, fetchAndUpdateChapterWithUserData, user]);

  const handleMainSearch = useCallback((version, columnId, book, chapter) => {
    setColumns((prevColumns) => {
      const existingColumn = prevColumns[columnId];
      // Skip fetch if the chapter is already loaded and has data
      if (
        existingColumn &&
        existingColumn.version === version &&
        existingColumn.book === book &&
        existingColumn.chapter === chapter &&
        Array.isArray(existingColumn.verses) &&
        existingColumn.verses.length > 0
      ) {
        console.log("Data already exists. Skipping fetch.");
        return prevColumns;
      }

      const updatedColumns = [...prevColumns];
      updatedColumns[columnId] = {
        ...normalizeColumn(existingColumn, columnId),
        version,
        book,
        chapter,
        loading: true,
        error: null,
      };
      userDataFetchSignatureRef.current = user ? getColumnsFetchSignature(updatedColumns) : '';
      return updatedColumns;
    });

    dispatch(fetchChapter({ book, chapter, version }))
      .then((result) => {
        if (result.error) {
          throw new Error(result.error.message);
        }

        setColumns((prevColumns) => {
          const updatedColumns = [...prevColumns];
          updatedColumns[columnId] = {
            ...normalizeColumn(updatedColumns[columnId], columnId),
            verses: result.payload,
            loading: false,
            error: null,
          };

      saveColumnsToLocalStorage(updatedColumns);
          userDataFetchSignatureRef.current = '';
          return updatedColumns;
        });

        if (user) {
          fetchDataForColumn(columnId, version, book, chapter).then((userData) => {
            setColumns((prevColumns) => {
              const updatedColumns = [...prevColumns];
              updatedColumns[columnId] = {
                ...normalizeColumn(updatedColumns[columnId], columnId),
                ...userData,
              };
              userDataFetchSignatureRef.current = getColumnsFetchSignature(updatedColumns);
              return updatedColumns;
            });
          });
        }

        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .catch(() => {
        setColumns((prevColumns) => {
          const updatedColumns = [...prevColumns];
          updatedColumns[columnId] = {
            ...normalizeColumn(updatedColumns[columnId], columnId),
            loading: false,
            error: "Failed to fetch verses",
          };
          return updatedColumns;
        });
      });
  }, [dispatch, fetchDataForColumn, getColumnsFetchSignature, normalizeColumn, saveColumnsToLocalStorage, user]);

  const handleInterlinearToggle = useCallback(async () => {
    const nextValue = !showInterlinear;
    setShowInterlinear(nextValue);

    if (!user) {
      return;
    }

    try {
      await dispatch(updateUserSettings({ showInterlinear: nextValue })).unwrap();
    } catch (error) {
      console.error("Error updating interlinear setting:", error);
      setShowInterlinear(!nextValue);
    }
  }, [dispatch, showInterlinear, user]);


  // Handle initialization via URL parameters
  useEffect(() => {
    if (hasURLParams) {
      const chapter = parseInt(paramChapter, 10);
      const nextSignature = `${paramVersion}:${paramBook}:${chapter}`;
      if (initialLoadSignatureRef.current === nextSignature) {
        return;
      }
      initialLoadSignatureRef.current = nextSignature;
      fetchChapterHandler(paramVersion, paramBook, chapter, "url");
    }
  }, [fetchChapterHandler, hasURLParams, paramBook, paramChapter, paramVersion]);

  // Default initialization (localStorage or default columns)
  useEffect(() => {
    if (!hasURLParams && !isInitialized) {
      const savedColumns = loadSavedColumns();
      if (savedColumns.length > 0) {
        updateColumnsSafely(savedColumns, "localStorage");
      } else {
        fetchChapterHandler(defaultVersion, defaultBook, 1, "default");
      }
      setIsInitialized(true);
    }
  }, [defaultBook, defaultVersion, fetchChapterHandler, hasURLParams, isInitialized, loadSavedColumns, updateColumnsSafely]);


  // Save columns to localStorage when not updated by URL
  useEffect(() => {
    if (lastUpdatedSource !== "url" && columns.length > 0) {
      try {
        localStorage.setItem("columns", JSON.stringify(columns));
        console.log("[Save to LocalStorage] Columns saved.");
      } catch (error) {
        console.error("Error saving columns to localStorage:", error);
      }
    }
  }, [columns, lastUpdatedSource]);

  // Update columns with user-specific data when user logs in
  useEffect(() => {
    if (user && columns.length > 0) {
      const fetchSignature = getColumnsFetchSignature(columns);

      if (userDataFetchSignatureRef.current === fetchSignature) {
        return;
      }

      console.log("[User Logged In] Updating columns with user-specific data...");
      const fetchUserData = async () => {
        const userDataByColumnId = await Promise.all(
          columns.map(async (col, idx) => {
            const { version, book, chapter } = col;
            const userData = await fetchDataForColumn(idx, version, book, chapter);
            return { columnId: col.id, userData };
          })
        );

        updateColumnsSafely((prevColumns) =>
          prevColumns.map((col, index) => {
            const matchedColumn = userDataByColumnId.find((entry) => entry.columnId === col.id);
            if (!matchedColumn) {
              return col;
            }

            return {
              ...normalizeColumn(col, index),
              ...matchedColumn.userData,
            };
          }),
        "user");
        userDataFetchSignatureRef.current = fetchSignature;
      };
      fetchUserData();
    }
  }, [columns, fetchDataForColumn, getColumnsFetchSignature, normalizeColumn, updateColumnsSafely, user]);

  const closeAlert = () => {
    setIsAlertOpen(false)
    setAlertMessage(""); // Clear the alert message to hide the alert
    setAlertAutoDismissMs(null);
  };

  // Update book options based on selected version
  const getBookOptions = (version) => {
    if (isKoreanVersion(version)) {
      return Object.values(krvBookNames);  // Korean book names
    }
    return books;  // English book names
  };



  // Helper function to extract pure verse text
  const extractPureVerseText = (columns, columnId, verseId) => {
    const column = columns.find((col, idx) => idx === columnId);
    if (!column) {
      console.error("Column not found with the specified columnId.");
      return '';
    }

    const verse = column.verses.find((v) => v.verse_id === verseId)?.text;
    if (!verse) {
      console.error("Verse not found in the specified column.");
      return '';
    }

    const titleMatch = verse.match(/^<([^>]+)>\s*(.*)$/);
    return titleMatch?.[2] || verse;
  };

  // Helper function to set related notes and sermons
  const setRelatedNotesAndSermons = (columns, columnId, verseId, setNote, setSermon) => {
    const column = columns[columnId];

    if (column) {
      const notes = Array.isArray(column.notes) ? column.notes : [];
      const sermons = Array.isArray(column.sermons) ? column.sermons : [];

      const note = notes.find((note) => note.verse_id === verseId);
      const sermon = sermons.find((sermon) => sermon.start_verse_id === verseId);

      setNote(note || null);
      setSermon(sermon || null);
    }
  };

  // Helper function to calculate popup position
  const calculatePopupPosition = (event, menuWidth = 200, menuHeight = 170) => {
    const { clientX, clientY } = event;
    const verticalOffset = -54;
    let top = clientY + window.scrollY + verticalOffset;
    let left = clientX + window.scrollX - 10;

    if (left + menuWidth > window.innerWidth) {
      left = window.innerWidth - menuWidth - 5;
    }
    if (clientY + menuHeight > window.innerHeight) {
      top = clientY + window.scrollY - menuHeight - verticalOffset;
    }

    return { top, left };
  };

  const calculatePopupPositionFromRect = (rect, menuWidth = 200, menuHeight = 170) => {
    if (!rect) return popupPosition;
    const verticalOffset = -54;
    // Prefer placing just below the selection to avoid covering the verse text
    let top = rect.bottom + window.scrollY + verticalOffset;
    let left = rect.left + window.scrollX - 10;

    if (left + menuWidth > window.innerWidth) {
      left = window.innerWidth - menuWidth - 5;
    }
    if (top + menuHeight > window.innerHeight) {
      // Not enough space below; place above the selection instead
      top = rect.top + window.scrollY - menuHeight - verticalOffset;
    }

    return { top, left };
  };

  const closePopupMenu = () => {
    setIsMenuOpen(false);
    setShowColorOptions(false);
    setActiveHighlightAction(null);
    setVerseHighlightAction(null);
  };

  const repositionMenuToSelection = () => {
    if (lastSelectionRect) {
      setPopupPosition(calculatePopupPositionFromRect(lastSelectionRect));
    }
  };

  const handleVerseClick = (event, verseId, book, chapter, version, columnId, verseElement = null) => {
    setSelectedVerseId(verseId);
    setSelectedBook(book);
    setSelectedChapter(chapter);
    setSelectedVersion(version);
    setShowColorOptions(false);
    setActiveHighlightAction(null);
    setVerseHighlightAction(null);

    // Preprocess and normalize verse text
    const rawVerseText = extractPureVerseText(columns, columnId, verseId);
    const normalizeWhitespace = (value = '') => value.replace(/\s+/g, ' ');
    const normalizedVerseText = normalizeWhitespace(rawVerseText);
    const pureVerseText = normalizedVerseText.trim();

    setRelatedNotesAndSermons(columns, columnId, verseId, setCurrentNote, setCurrentSermon);

    const targetElement = verseElement instanceof Node
      ? verseElement
      : (event?.currentTarget instanceof Node ? event.currentTarget : null);

    const visibleVerseText = normalizeWhitespace(targetElement?.textContent || pureVerseText).trim();
    setVerseText(visibleVerseText);

    const detectSelectedTextAndOffsets = (targetElement) => {
      const verseContent = normalizeWhitespace(targetElement?.textContent || pureVerseText).trim();
      const fallbackSelection = {
        text: verseContent,
        startOffset: 0,
        endOffset: verseContent.length,
      };

      if (!targetElement) return fallbackSelection;

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
        return fallbackSelection;
      }

      const originalRange = selection.getRangeAt(0);

      // At least one endpoint must sit inside the verse. If the drag starts in
      // this verse but releases outside (e.g., past the final character), clip
      // the range to the verse's text so we still get a partial selection.
      const startInside = targetElement.contains(originalRange.startContainer);
      const endInside = targetElement.contains(originalRange.endContainer);
      if (!startInside && !endInside) {
        return fallbackSelection;
      }

      const range = originalRange.cloneRange();
      if (!startInside) {
        range.setStart(targetElement, 0);
      }
      if (!endInside) {
        range.setEnd(targetElement, targetElement.childNodes.length);
      }

      // Calculate offsets based on text content length
      const preRange = range.cloneRange();
      preRange.selectNodeContents(targetElement);
      preRange.setEnd(range.startContainer, range.startOffset);

      const selectedTextRaw = range.toString();
      const normalizedText = normalizeWhitespace(selectedTextRaw).trim();
      if (!normalizedText) return fallbackSelection;

      const startOffset = preRange.toString().length;
      const endOffset = startOffset + selectedTextRaw.length;

      console.log("[Selection] raw:", selectedTextRaw, "normalized:", normalizedText, "start", startOffset, "end", endOffset);
      return { text: normalizedText, startOffset, endOffset };
    };

    const selection = detectSelectedTextAndOffsets(targetElement);

    if (!selection.text.trim()) {
      // No valid selection, fallback to highlighting the entire verse
      setSelectedText(visibleVerseText);
      setStartOffset(0);
      setEndOffset(visibleVerseText.length);
      selectionSnapshotRef.current = {
        selectedText: visibleVerseText,
        startOffset: 0,
        endOffset: visibleVerseText.length,
        verseText: visibleVerseText,
      };
      console.log("[Selection] No text -> using full verse length", visibleVerseText.length);
    } else {
      // Highlight only the selected text
      setSelectedText(selection.text);
      setStartOffset(selection.startOffset);
      setEndOffset(selection.endOffset);
      selectionSnapshotRef.current = {
        selectedText: selection.text,
        startOffset: selection.startOffset,
        endOffset: selection.endOffset,
        verseText: visibleVerseText,
      };
      console.log("[Selection] Stored", selection);

      // console.log("Selected Text:", selection.text);
      // console.log("Start Offset:", selection.startOffset);
      // console.log("End Offset:", selection.endOffset);
    }

    // Remember selection bounds for stable popup positioning
    const selectionRange = (() => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || !verseElement) return null;
      const range = selection.getRangeAt(0);
      const startInside = verseElement.contains(range.startContainer);
      const endInside = verseElement.contains(range.endContainer);
      if (!startInside && !endInside) return null;
      const clipped = range.cloneRange();
      if (!startInside) clipped.setStart(verseElement, 0);
      if (!endInside) clipped.setEnd(verseElement, verseElement.childNodes.length);
      return clipped;
    })();

    const rangeRect = selectionRange
      ? selectionRange.getBoundingClientRect()
      : verseElement?.getBoundingClientRect();

    setLastSelectionRect(rangeRect);

    const popupPosition = rangeRect
      ? calculatePopupPositionFromRect(rangeRect)
      : calculatePopupPosition(event);
    console.log("[Popup] rect", rangeRect, "pos", popupPosition);
    setPopupPosition(popupPosition);
    setIsMenuOpen(true);
  };

  const handleHighlight = (colorChoice) => {
    const selectionSnapshot = selectionSnapshotRef.current;
    const currentSelectedText = selectionSnapshot.selectedText || selectedText;
    const currentStartOffset = Number.isFinite(selectionSnapshot.startOffset) ? selectionSnapshot.startOffset : startOffset;
    const currentEndOffset = Number.isFinite(selectionSnapshot.endOffset) ? selectionSnapshot.endOffset : endOffset;
    const currentVerseText = selectionSnapshot.verseText || verseText;

    console.log("[Highlight] start with selection", {
      selectedText: currentSelectedText,
      startOffset: currentStartOffset,
      endOffset: currentEndOffset,
      verseText: currentVerseText,
      colorChoice,
    });
    closePopupMenu();

    if (!user) {
      setIsAlertOpen(true);
      return;
    }

    // Guard: need valid selection to create partial highlight
    const hasSelection =
      currentSelectedText && currentSelectedText.trim().length > 0 && currentEndOffset > currentStartOffset;
    if (!hasSelection) {
      setAlertMessage(`No text selected to highlight. startOffset=${currentStartOffset}, endOffset=${currentEndOffset}`);
      setIsAlertOpen(true);
      return;
    }

    const appliedColor = colorChoice || highlightColor || '#fef08a';

    if (mappingHighlight && mappingHighlight.verseId === selectedVerseId) {
      dispatch(addHighlightSegment({
        groupId: mappingHighlight.groupId,
        segment: {
          version: selectedVersion,
          book: selectedBook,
          chapter: selectedChapter,
          selectedText: currentSelectedText,
          startOffset: currentStartOffset,
          endOffset: currentEndOffset,
        },
      })).unwrap().then(() => {
        setMappingHighlight(null);
        closePopupMenu();
        return dispatch(fetchHighlightsByVerse({ version: selectedVersion, book: selectedBook, chapter: selectedChapter, verseId: selectedVerseId }));
      }).then((result) => {
        const refreshed = result?.payload?.highlights || [];
        setColumns((prevColumns) => prevColumns.map((column) => (
          column.book === selectedBook && column.chapter === selectedChapter
            ? {
                ...column,
                highlights: [
                  ...(column.highlights || []).filter((item) => Number(item.verse_id) !== Number(selectedVerseId)),
                  ...refreshed,
                ],
              }
            : column
        )));
      }).catch((error) => {
        setAlertMessage(error.message || 'Failed to map highlight.');
        setIsAlertOpen(true);
      });
      return;
    }

    if (activeHighlightAction?.highlight) {
      const { highlight } = activeHighlightAction;
      const replacementHighlight = {
        version: selectedVersion,
        book: selectedBook,
        chapter: selectedChapter,
        verseId: selectedVerseId,
        selectedText: highlight.selected_text || currentSelectedText,
        startOffset: Number(highlight.start_offset),
        endOffset: Number(highlight.end_offset),
        scope: highlight.scope || 'text',
        color: appliedColor,
      };

      dispatch(deleteHighlight(highlight.id))
        .unwrap()
        .then(() =>
          dispatch(addHighlight({
            chapterKey: makeHighlightChapterKey(selectedVersion, selectedBook, selectedChapter),
            newHighlight: replacementHighlight,
          })).unwrap()
        )
        .then((result) => {
          const newHighlight = result?.highlight || result;
          setColumns((prevColumns) =>
            prevColumns.map((col) => {
              if (col.book === selectedBook && col.chapter === selectedChapter) {
                const existingHighlights = Array.isArray(col.highlights) ? col.highlights : [];
                return {
                  ...col,
                  highlights: existingHighlights
                    .filter((item) => item.id !== highlight.id)
                    .concat(newHighlight),
                };
              }
              return col;
            })
          );
        })
        .catch((error) => {
          console.error("Failed to update highlight color:", error);
          setAlertMessage("Failed to update highlight color.");
          setIsAlertOpen(true);
        });
      return;
    }

    const addHighlightLocally = (payload, replaceTempId = null) => {
      setColumns((prevColumns) =>
        prevColumns.map((col) => {
          if (col.book === selectedBook && col.chapter === selectedChapter) {
            const existing = col.highlights || [];
            const updated = replaceTempId
              ? existing.map((h) =>
                  h.id === replaceTempId ? { ...payload, color: payload.color || appliedColor } : h
                )
              : [...existing, { ...payload, color: payload.color || appliedColor }];
            return { ...col, highlights: updated };
          }
          return col;
        })
      );
    };

    const createHighlight = (offsets = { start: currentStartOffset, end: currentEndOffset }) => {
      const tempId = `temp-${Date.now()}`;
      const highlightData = {
        version: selectedVersion,
        book: selectedBook,
        chapter: selectedChapter,
        verseId: selectedVerseId,
        selectedText: currentSelectedText,
        startOffset: offsets.start,
        endOffset: offsets.end,
        scope: offsets.start === 0 && offsets.end >= currentVerseText.length ? 'verse' : 'text',
        color: appliedColor,
      };

      // Optimistic UI update
      addHighlightLocally({ ...highlightData, id: tempId });

      return dispatch(addHighlight({
        chapterKey: makeHighlightChapterKey(selectedVersion, selectedBook, selectedChapter),
        newHighlight: highlightData,
      })).then((res) => {
        if (res.payload) {
          const savedHighlight = res.payload.highlight || res.payload;
          console.log("[Highlight] Added (server)", savedHighlight);
          addHighlightLocally(savedHighlight, tempId);
          // Refresh highlights for this verse to ensure UI shows latest state
          dispatch(fetchHighlightsByVerse({
            version: selectedVersion,
            book: selectedBook,
            chapter: selectedChapter,
            verseId: selectedVerseId,
          })).then((fetchRes) => {
            const refreshed = fetchRes?.payload?.highlights || [];
            console.log("[Highlight] Refetched highlights for verse", selectedVerseId, refreshed);
            setColumns((prevColumns) =>
              prevColumns.map((col) => {
                if (col.book === selectedBook && col.chapter === selectedChapter) {
                  return {
                    ...col,
                    highlights: Array.isArray(col.highlights)
                      ? col.highlights
                          .filter((h) => h.verse_id !== selectedVerseId)
                          .concat(refreshed)
                      : refreshed,
                  };
                }
                return col;
              })
            );
          });
        } else {
          setAlertMessage("Failed to add highlight.");
          setIsAlertOpen(true);
          console.warn("[Highlight] addHighlight returned no payload", res);
        }
      }).catch((error) => {
        console.error("Highlight dispatch error:", error);
        setAlertMessage("Failed to add highlight.");
        setIsAlertOpen(true);
      });
    };

    dispatch(fetchHighlightsByVerse({
      version: selectedVersion,
      book: selectedBook,
      chapter: selectedChapter,
      verseId: selectedVerseId,
    })).then((res) => {
      const highlights = res?.payload?.highlights || [];
      console.log("[Highlight] Existing highlights for verse", selectedVerseId, highlights);

      if (currentSelectedText.length > 0 && currentSelectedText.length < currentVerseText.length) {
        const isExactDuplicate = highlights.some(
          (highlight) =>
            Number(highlight.start_offset) === Number(currentStartOffset) &&
            Number(highlight.end_offset) === Number(currentEndOffset)
        );
        if (isExactDuplicate) {
          setAlertMessage("This selection is already highlighted.");
          setIsAlertOpen(true);
          return;
        }
        return createHighlight();
      } else {
        // Full verse path
        if (highlights.length > 0) {
          setHighlightsForVerse(highlights);
          setIsHighlightModalOpen(true);
          return;
        }
        // Add whole verse
        return createHighlight({ start: 0, end: currentSelectedText.length });
      }
    }).catch((error) => {
      console.error("Error fetching highlights for verse:", error);
      // Fallback: still attempt to add the highlight without duplicate check
      createHighlight();
    });
  };

  const openColorOptions = () => {
    if (!user) {
      setIsAlertOpen(true);
      return;
    }
    repositionMenuToSelection();
    setShowColorOptions(true);
  };


  // Function to delete a highlight
  const handleDeleteHighlight = (highlightId) => {
    dispatch(deleteHighlight(highlightId)).then((res) => {
      if (res.payload) {
        setColumns((prevColumns) =>
          prevColumns.map((col) => {
            if (col.book === selectedBook && col.chapter === selectedChapter) {
              return {
                ...col,
                highlights: col.highlights.filter(
                  (highlight) => highlight.id !== highlightId
                ),
              };
            }
            return col;
          })
        );
        if (
          activeHighlightAction?.highlight?.id === highlightId ||
          verseHighlightAction?.highlight?.id === highlightId
        ) {
          closePopupMenu();
        }
      } else {
        console.error("Error deleting highlight:", res.error);
      }
    });
  };

  const handleExistingHighlightClick = (event, highlight, verseId, book, chapter, version, columnId, verseElement = null) => {
    event.preventDefault();
    event.stopPropagation();

    const normalizeWhitespace = (value = '') => value.replace(/\s+/g, ' ').trim();
    const visibleVerseText = normalizeWhitespace(verseElement?.textContent || '');
    const highlightStart = Number(highlight.start_offset) || 0;
    const highlightEnd = Number(highlight.end_offset) || 0;
    const isFullVerseHighlight = (
      highlightStart === 0 &&
      highlightEnd > 0 &&
      highlightEnd >= visibleVerseText.length
    );

    setSelectedVerseId(verseId);
    setSelectedBook(book);
    setSelectedChapter(chapter);
    setSelectedVersion(version);
    setSelectedText(highlight.selected_text || "");
    setStartOffset(highlightStart);
    setEndOffset(highlightEnd);
    setVerseText(visibleVerseText);
    selectionSnapshotRef.current = {
      selectedText: highlight.selected_text || visibleVerseText,
      startOffset: highlightStart,
      endOffset: highlightEnd,
      verseText: visibleVerseText,
    };

    if (isFullVerseHighlight) {
      setActiveHighlightAction(null);
      setVerseHighlightAction({ highlight, columnId });
    } else {
      setVerseHighlightAction(null);
      setActiveHighlightAction({ highlight, columnId });
    }

    const targetRect = event.target?.getBoundingClientRect?.();
    const fallbackRect = verseElement?.getBoundingClientRect?.();
    const rect = targetRect || fallbackRect;

    if (rect) {
      setLastSelectionRect(rect);
      setPopupPosition(calculatePopupPositionFromRect(rect));
    } else {
      setPopupPosition(calculatePopupPosition(event));
    }

    setShowColorOptions(false);
    setIsMenuOpen(true);
  };


  // Function to handle copying the verse text
  const handleCopyVerse = () => {
    const column = columns.find(
      (col) =>
        col.version === selectedVersion && // Check for the matching version
        col.verses.some((v) => v.verse_id === selectedVerseId)
    );

    const verse = column?.verses.find((v) => v.verse_id === selectedVerseId)?.text || '';

    if (verse) {
      const verseNumber = selectedVerseId % 1000;
      const versionLabel = versionOptions.find((v) => v.value === selectedVersion)?.label || selectedVersion;
      navigator.clipboard.writeText(`${selectedBook} ${selectedChapter}:${verseNumber} ${verse}(${versionLabel})`);
      setAlertMessage(`${selectedVersion} - ${selectedBook} ${selectedChapter}:${verseNumber} copied to clipboard!`);
      setAlertAutoDismissMs(2000);
      setIsAlertOpen(true);
    } else {
      setAlertMessage('Verse not found or version mismatch.');
    }

    closePopupMenu();
  };


  // Function to fetch highlights once per chapter

  // Function to handle adding a new column with the selected version
  const handleAddColumn = (version) => {
    if (columns.length < MAX_VERSIONS) {
      const firstColumnBook = columns[0]?.book || 'Genesis'; // Fallback to 'Genesis' if no columns are present
      const firstIsKorean = columns[0]?.version && isKoreanVersion(columns[0].version);

      let defaultBook;
      if (isKoreanVersion(version)) {
        // Target column is Korean
        if (firstIsKorean) {
          defaultBook = firstColumnBook; // already Korean
        } else {
          defaultBook = krvBookNames[firstColumnBook] || '창세기';
        }
      } else {
        // Target column is English
        if (firstIsKorean) {
          defaultBook = koreanBookNames[firstColumnBook] || firstColumnBook;
        } else {
          defaultBook = firstColumnBook;
        }
      }
      const newColumn = {
        version,
        book: defaultBook,
        chapter: columns[0]?.chapter || 1,
        id: Date.now(),
        verses: [],
        notes: [],
        highlights: [],
        sermons: [],
        loading: true,
        error: null,
      };
      console.log("new colun", newColumn)

      setColumns((prevColumns) => {
        const updatedColumns = [...prevColumns, newColumn];
        userDataFetchSignatureRef.current = user ? getColumnsFetchSignature(updatedColumns) : '';
        return updatedColumns;
      });

      dispatch(fetchChapter({ book: newColumn.book, chapter: newColumn.chapter, version }))
        .then((result) => {
          if (result.error) {
            throw new Error(result.error.message);
          }

          setColumns((prevColumns) =>
            prevColumns.map((col) =>
              col.id === newColumn.id
                ? {
                    ...normalizeColumn(col),
                    verses: result.payload,
                    loading: false,
                    error: null,
                  }
                : col
            )
          );

          if (user) {
            fetchDataForColumn(newColumn.id, newColumn.version, newColumn.book, newColumn.chapter).then((userData) => {
              setColumns((prevColumns) =>
                {
                  const updatedColumns = prevColumns.map((col) =>
                    col.id === newColumn.id
                      ? {
                          ...normalizeColumn(col),
                          ...userData,
                        }
                      : col
                  );
                  userDataFetchSignatureRef.current = getColumnsFetchSignature(updatedColumns);
                  return updatedColumns;
                }
              );
            });
          }
        })
        .catch((error) => {
          console.error("Error loading added column:", error);
          setColumns((prevColumns) =>
            prevColumns.map((col) =>
              col.id === newColumn.id
                ? {
                    ...normalizeColumn(col),
                    loading: false,
                    error: "Failed to fetch verses",
                  }
                : col
            )
          );
        });
    }
    setSelectedTab(columns.length)
    setIsVersionMenuOpen(false);
  };

  // Remove a column
  const removeColumn = (id) => {
    setColumns(columns.filter(column => column.id !== id));
  };

  // Function to handle making notes
  const handleMakeNote = () => {
    closePopupMenu();
    if (!user) {
      // setAlertMessage(null);
      setIsAlertOpen(true)
    } else {
      setModalOpen(true);
    }
  };

  // Function to handle saving a note
  const handleSaveNote = async (text, title) => {
    let newNote = null;

    try {
      if (currentNote) {
        // Update existing note
        const result = await dispatch(updateNote({ id: currentNote.id, text, title, book: currentNote.book, version: currentNote.version || selectedVersion })).unwrap();
        newNote = { ...currentNote, ...result };
      } else {
        // Add a new note
        const result = await dispatch(addNote({ userId: user.id, title, verseId: selectedVerseId, content: text, book: selectedBook, version: selectedVersion })).unwrap();
        newNote = result; // Capture the new note from the result
      }

      // Update the local columns state to reflect changes immediately
      setColumns((prevColumns) => {
        return prevColumns.map((col) => {
          // Check if the column contains the verse where the note was added/updated
          if (col.verses.some((v) => v.verse_id === selectedVerseId)) {
            const updatedNotes = col.notes ? [...col.notes] : [];

            if (currentNote) {
              // Update the existing note in the notes array
              const noteIndex = updatedNotes.findIndex((n) => n.verse_id === selectedVerseId);
              if (noteIndex !== -1) {
                updatedNotes[noteIndex] = newNote; // Update the note
              }
            } else {
              // Add the new note to the notes array
              updatedNotes.push(newNote);
            }

            return { ...col, notes: updatedNotes }; // Update the column's notes
          }
          return col;
        });
      });

      // Update the currentNote state so that the modal shows the updated content
      setCurrentNote(newNote);
    } catch (error) {
      console.error('Error saving note:', error);
    } finally {
      // setModalOpen(false); // Close the modal after saving
    }
  };

  // Function to handle deleting a note
  const handleDeleteNote = async () => {
    if (currentNote) {
      await dispatch(deleteNote(currentNote.id));
      setColumns((prevColumns) => {
        return prevColumns.map((col) => {
          if (col.verses.some((v) => v.verse_id === selectedVerseId)) {
            const updatedNotes = col.notes.filter((n) => n.verse_id !== selectedVerseId); // Remove the deleted note
            return { ...col, notes: updatedNotes };
          }
          return col;
        });
      });
    }
    setModalOpen(false);
  };

  const handleSermon = () => {
    closePopupMenu();  // Close the popup menu
    if (!user) {
      setIsAlertOpen(true);
    } else {
      setIsSermonEditorOpen(true);  // Correctly set the state to open the sermon editor
      console.log("selected verseid", selectedVerseId)
    }
  };

  const handleNoteClick = (id) => {
    dispatch(fetchNoteById(id)) // Dispatch the action to fetch note data
      .then((result) => {
        if (result.meta.requestStatus === 'fulfilled') {
          setSelectedBook(result.payload.book)
          const chapter = Math.floor((result.payload.verse_id % 1000000) / 1000);
          setSelectedChapter(chapter)
          setSelectedVerseId(result.payload.verse_id)
          setCurrentNote(result.payload)
          setModalOpen(true); // Open the modal after successfully fetching the note
        } else {
          console.error('Failed to fetch note:', result.error);
        }
      })
      .catch((error) => {
        console.error('Error fetching note:', error);
      });
  };

  const handleSermonClick = (id) => {
    dispatch(fetchSermonById(id))
      .then((result) => {
        if (result.meta.requestStatus === 'fulfilled') {
          setCurrentSermon(result.payload);
          setSelectedBook(result.payload.book);
          setSelectedChapter(result.payload.start_chapter);
          setSelectedVerseId(result.payload.start_verse_id);

          // Determine the correct tab index for the sermon
          const tabIndex = columns.findIndex((col) =>
            col.verses.some((v) => v.verse_id === result.payload.start_verse_id)
          );

          if (tabIndex !== -1) {
            setSelectedTab(tabIndex); // Set to the matching tab
            setCurrentTabIndex(tabIndex); // Track the current tab index
          } else {
            setSelectedTab(columns.length); // If not found, open a new tab
            setCurrentTabIndex(columns.length); // Track the new tab index
          }
          console.log("selected tab", selectedTab)
          console.log("currenet tab", currentTabIndex)
          setIsSermonEditorOpen(true); // Open the modal
        }
      })
      .catch((error) => {
        console.error('Error fetching sermon:', error);
      });
  };

  const renderVerseContent = ({
    titleContent,
    mainHtml,
    footnotes,
    interlinear,
    verseId,
    column,
    columnId,
    columnHighlights,
    columnNotes,
    columnSermons,
    currentVerseNumber,
    version,
  }) => {
    const note = columnNotes?.find((note) => note.verse_id === verseId); // Find the note object
    const sermon = columnSermons?.find((sermon) => sermon.start_verse_id === verseId); // Find the sermon object
    const safeFootnotes = Array.isArray(footnotes) ? footnotes : [];
    const safeInterlinear = Array.isArray(interlinear) ? interlinear : [];
    const shouldUseFootnotes = safeFootnotes.length > 0;
    const interactiveMainHtml = shouldUseFootnotes
      ? mainHtml.replace(
          /<sup>(.*?)<\/sup>/g,
          (_, marker) => `<sup class="footnote-marker" data-footnote-marker="${marker}">${marker}</sup>`
        )
      : mainHtml;
    const activeMarker = openFootnote?.verseId === verseId ? openFootnote.marker : null;
    const activeFootnote = activeMarker
      ? safeFootnotes.find((footnote) => footnote.marker === activeMarker) || safeFootnotes[0]
      : null;

    const shouldRenderInterlinear = showInterlinear && version === 'ESV' && safeInterlinear.length > 0;

    return (
      <React.Fragment key={`verse-${verseId}`}>
        {titleContent && <div className="my-4 font-bold text-m">{titleContent}</div>}
        <div className="mb-2">
            <InteractiveVerse
              verse={{ verse_id: verseId, book: column.book, chapter: column.chapter, version, verse: currentVerseNumber, text: interactiveMainHtml }}
              verseText={interactiveMainHtml}
              highlights={columnHighlights?.filter((highlight) => Number(highlight.verse_id) === Number(verseId))}
              onVerseClick={handleVerseClick}
              memo={note}
              hasSermon={sermon}
              columnId={columnId}
              isMenuActive={
              isMenuOpen &&
              Number(selectedVerseId) === Number(verseId)
            }
              highlightColor={highlightColor}
              onNoteClick={handleNoteClick}
              onSermonClick={handleSermonClick}
              onHighlightClick={handleExistingHighlightClick}
              onUnmappedHighlightClick={(event, highlight) => {
                event.preventDefault();
                event.stopPropagation();
                const versions = (highlight?.mapped_versions || []).join(', ');
                setMappingHighlight({ groupId: highlight.group_id, verseId: Number(highlight.verse_id) });
                setAlertMessage(`Mapped in ${versions || 'another translation'}. Select the matching words in this verse, then choose Highlight.`);
                setIsAlertOpen(true);
              }}
              onFootnoteMarkerClick={(marker, event) => {
                event.preventDefault();
                event.stopPropagation();
                setOpenFootnote((prev) =>
                  prev && prev.verseId === verseId && prev.marker === marker ? null : { verseId, marker }
                );
              }}
            />
          {activeFootnote?.content && (
            <div className="footnote-inline">
              {activeFootnote.content}
            </div>
          )}
          {shouldRenderInterlinear && (
            <div className="interlinear-grid">
              {safeInterlinear.map((token, idx) => (
                <div key={`${verseId}-interlinear-${idx}`} className="interlinear-token">
                  <span className="interlinear-english">{token.english || token.gloss || '\u00A0'}</span>
                  <span className="interlinear-original">{token.original || '\u00A0'}</span>
                  <span className="interlinear-transliteration">{token.transliteration || token.lemma_transliteration || '\u00A0'}</span>
                  {token.strong && <span className="interlinear-strong">{token.strong}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </React.Fragment>
    );
  };

  const renderVerses = (column, columnId) => {
    const verses = Array.isArray(column?.verses) ? column.verses : [];

    if (column.loading) return <p>Loading...</p>;
    if (column.error) return <p>Error: {column.error}</p>;
    if (verses.length === 0) return <p>No Bible verses available.</p>;

    // Ensure column.notes, column.highlights, and column.sermons are always arrays to prevent TypeError
    const columnNotes = Array.isArray(column.notes) ? column.notes : [];
    const columnHighlights = Array.isArray(column.highlights) ? column.highlights : [];
    const columnSermons = Array.isArray(column.sermons) ? column.sermons : [];
    let currentVerseNumber = 0;

    return verses.map(({ version, text, verse_id, footnotes: apiFootnotes, interlinear: apiInterlinear }, index) => {
      // Enhanced title detection: Extract title and verse text
      const titleMatch = text.match(/^<(?!span\b)([^>]+)>\s*(.*)$/); // Regex to separate title and verse text
      const titleContent = titleMatch?.[1]; // Extracted title (inside <...>)
      const verseTextRaw = titleMatch?.[2] || text; // Remaining verse text after title or original text

      // Split footnotes from main text (새번역 uses variants like "<br>▷", "<br/>▷", or "▷" alone)
      const [mainHtml, ...footParts] = verseTextRaw.split(/<br\s*\/?>\s*▷/i);
      const rawFootnotes =
        footParts.length > 0
          ? footParts.map((part) => `▷ ${part}`)
          : verseTextRaw.includes('▷')
            ? [verseTextRaw.slice(verseTextRaw.indexOf('▷'))]
            : [];

      // Parse footnotes into array {marker, content}
      const parsedTextFootnotes = rawFootnotes.flatMap((block) =>
        block
          .split(/<br\s*\/?>\s*▷\s*/i)
          .map((item) => item.replace(/^▷\s*/, '').trim())
          .filter(Boolean)
          .map((item, idx) => {
            const supMatch = item.match(/<sup>(.*?)<\/sup>/);
            const marker = supMatch ? supMatch[1] : `${idx + 1}`;
            const content = item.replace(/<sup>(.*?)<\/sup>/, '').trim();
            return { marker, content };
          })
      );

      const footnotes = Array.isArray(apiFootnotes) && apiFootnotes.length > 0
        ? apiFootnotes
        : parsedTextFootnotes;


      // Render verse content using the extracted function
      return renderVerseContent({
        titleContent,
        mainHtml,
        footnotes,
        interlinear: apiInterlinear,
        verseId: verse_id,
        column,
        columnId,
        columnHighlights,
        columnNotes,
        columnSermons,
        currentVerseNumber: ++currentVerseNumber, // Increment verse number
        version,
        key: `verse-${verse_id}-${index}`
      });
    });
  };

  // Handle clicks outside the popup menu and version menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside both the menu and the button
      if (
        versionMenuRef.current &&
        !versionMenuRef.current.contains(event.target) &&
        versionButtonRef.current &&
        !versionButtonRef.current.contains(event.target)
      ) {
        setIsVersionMenuOpen(false);
      }

      // Close the verse popup menu if the click is outside of it
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closePopupMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Synchronize all columns to the same book and chapter
  const handleSynchronize = () => {
    const firstColumn = columns[0]; // Use the book and chapter from the first column
    const isFirstKorean = isKoreanVersion(firstColumn.version);

    setColumns((prevColumns) => {
      return prevColumns.map((column) => {
        let syncedBook;

        if (isFirstKorean) {
          // If the first column is Korean, convert other versions to English
          syncedBook = isKoreanVersion(column.version) ? firstColumn.book : Object.keys(koreanBookNames).find(key => koreanBookNames[key] === firstColumn.book);
        } else {
          // If the first column is English, convert Korean versions to Korean book names
          syncedBook = isKoreanVersion(column.version) ? koreanBookNames[firstColumn.book] : firstColumn.book;
        }

        return {
          ...column,
          book: syncedBook || firstColumn.book, // Sync the book name, fallback to original if undefined
          chapter: firstColumn.chapter, // Sync the chapter
          loading: true, // Set loading state to true to indicate content is loading
        };
      });
    });
    // Trigger a search for each column after synchronization
    if (isKoreanVersion(columns[0].version)) {
      columns.forEach((column, index) => {
        let syncedBook;
        if (isKoreanVersion(column.version)) {

          syncedBook = column.book
        } else {
          syncedBook = koreanBookNames[columns[0].book];
        }
        handleMainSearch(column.version, index, syncedBook, firstColumn.chapter);
      });
    } else {
      columns.forEach((column, index) => {
        let syncedBook;
        if (isKoreanVersion(column.version)) {
          syncedBook = koreanBookNames[firstColumn.book];
        } else {
          syncedBook = columns[0].book;
        }
        handleMainSearch(column.version, index, syncedBook, firstColumn.chapter)
      });
    }
  };

  // Handle chapter navigation
  const handleChapterNavigation = (direction, columnId) => {
    const books = isKoreanVersion(columns[columnId].version) ? krvBooks : navigationBooks;
    navigateChapter(setColumns, direction, columnId, handleMainSearch, books);
  };

  const ColumnControls = ({
    column,
    index,
    handleVersionChange,
    handleBookChange,
    handleChapterChange,
    handleMainSearch,
    handleChapterNavigation,
  }) => {
    const inputRef = useRef(null); // Ref for the input box
    const [chapterValue, setChapterValue] = useState(column.chapter || ''); // Local state for input

    const handleInputChange = (e) => {
      const sanitizedValue = e.target.value.replace(/[^0-9]/g, ''); // Allow only numbers
      setChapterValue(sanitizedValue); // Update local state
    };

    const handleSearch = () => {
      handleChapterChange(index, chapterValue); // Update parent state
      handleMainSearch(column.version, index, column.book, chapterValue); // Trigger search
      inputRef.current?.focus(); // Retain focus after search
    };

    return (
      <div
        className="relative flex flex-wrap items-center gap-2 controls-container mb-4"
        style={{ maxWidth: '600px', width: '100%' }}
      >
        {/* First Row: Version, Book, Chapter */}
        <div className="flex flex-1 flex-wrap items-center gap-2 w-full">
          {/* Version Selection */}
          <select
            className="p-2 border border-gray-300 rounded-md text-sm flex-[1_1_20%] min-w-[65px] h-10 bible-v"
            value={column.version}
            onChange={(e) => handleVersionChange(index, e.target.value)}
          >
            {versionOptions.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>

          {/* Book Selection */}
          <select
            className="p-2 border border-gray-300 rounded-md text-sm flex-[1_1_30%] min-w-[90px] h-10 bible-b"
            value={column.book}
            onChange={(e) => handleBookChange(index, e.target.value)}
          >
            {getBookOptions(column.version).map((bookName) => (
              <option key={bookName} value={bookName}>
                {bookName}
              </option>
            ))}
          </select>

          {/* Chapter Input */}
          <input
            ref={inputRef}
            type="text"
            value={chapterValue}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            placeholder="chapter"
            className="p-2 border border-gray-300 rounded-md text-sm flex-[1_1_15%] min-w-[40px] text-center h-10 placeholder:text-xs bible-ch"
          />
        </div>

        {/* Second Row: enter Button, Navigation Buttons & Bookmark Manager */}
        <div className="flex flex-1 flex-wrap items-center gap-2 w-full second-c">
          {/* enter Button */}
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              handleSearch();
            }}
            className="text-white px-4 py-2 rounded-full hover:bg-[#782c0a] bg-[#983b10] flex-[1_1_30%] min-w-[60px] h-10 shadow-[-3px_-3px_7px_var(--outer-shadow-light),4px_5px_10px_rgba(0,0,0,0.18)] active:shadow-[inset_2px_2px_5px_rgba(0,0,0,0.3),inset_-2px_-2px_5px_rgba(255,255,255,0.2)] transition-all search-bt"
          >
            {t('enter')}
          </button>

          {/* Navigation Buttons */}
          <div className="flex space-x-1 flex-[1_1_20%] justify-center navigation-ch">
            <button
              onClick={() => handleChapterNavigation("previous", index)}
              className="text-black w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] hover:shadow-[-1px_-1px_3px_var(--outer-shadow-light),1px_1px_3px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_4px_var(--outer-shadow-dark),inset_-2px_-2px_4px_var(--outer-shadow-light)] transition-all navigation-icon"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.25 19.25L8.75 12.75L15.25 6.25"
                />
              </svg>
            </button>
            <button
              onClick={() => handleChapterNavigation("next", index)}
              className="text-black w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] hover:shadow-[-1px_-1px_3px_var(--outer-shadow-light),1px_1px_3px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_4px_var(--outer-shadow-dark),inset_-2px_-2px_4px_var(--outer-shadow-light)] transition-all navigation-icon"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8.75 19.25L15.25 12.75L8.75 6.25"
                />
              </svg>
            </button>

          </div>

          {/* Bookmark Manager */}
          <div className="bookmark">
            <BookmarkManager
              columnId={index}
              column={column}
              onNavigate={(columnId, book, chapter, version) => {
                setColumns((prevColumns) =>
                  prevColumns.map((col, idx) => {
                    if (idx === columnId) {
                      handleMainSearch(version, columnId, book, chapter);
                      return { ...col, book, chapter, version };
                    }
                    return col;
                  })
                );
              }}
            />
          </div>
        </div>

      </div>
    );
  };

  return (

    <div className="p-4 relative" data-theme={theme}>
      {/* Version Button with Dropdown */}
      {loading ? (
        <p>Loading user settings...</p> // Loading indicator
      ) : (
        <>
          <div className="relative mb-4 m-top-control">
            <button
              ref={versionButtonRef}
              onClick={() => setIsVersionMenuOpen(!isVersionMenuOpen)}
              className="bg-lightYellow text-white px-4 h-10 rounded-full hover:bg-darkYellow shadow-[-3px_-3px_7px_var(--outer-shadow-light),4px_5px_10px_rgba(0,0,0,0.18)] active:shadow-[inset_2px_2px_5px_rgba(0,0,0,0.3),inset_-2px_-2px_5px_rgba(255,255,255,0.2)] transition-all whitespace-nowrap m-add-version"
            >
              Add Version
            </button>
            <button
              onClick={handleSynchronize}
              className="bg-lightDarkBlue text-white px-4 h-10 rounded-full hover:bg-darkBlue ml-2 shadow-[-3px_-3px_7px_var(--outer-shadow-light),4px_5px_10px_rgba(0,0,0,0.18)] active:shadow-[inset_2px_2px_5px_rgba(0,0,0,0.3),inset_-2px_-2px_5px_rgba(255,255,255,0.2)] transition-all whitespace-nowrap m-synch"
            >
              Sync
            </button>
            <button
              onClick={handleInterlinearToggle}
              aria-pressed={showInterlinear}
              className={`text-white px-4 h-10 rounded-full ml-2 shadow-[-3px_-3px_7px_var(--outer-shadow-light),4px_5px_10px_rgba(0,0,0,0.18)] active:shadow-[inset_2px_2px_5px_rgba(0,0,0,0.3),inset_-2px_-2px_5px_rgba(255,255,255,0.2)] transition-all whitespace-nowrap ${showInterlinear ? 'bg-amber-700 hover:bg-amber-800' : 'bg-slate-500 hover:bg-slate-600'}`}
            >
              Interlinear
            </button>
            {isVersionMenuOpen && (
              <div
                ref={versionMenuRef}
                className="absolute top-full left-0 mt-2 w-[7.5rem] bg-white rounded-[1.25rem] p-2 z-50 shadow-[-6px_-6px_14px_var(--outer-shadow-light),6px_6px_14px_var(--outer-shadow-dark)]"
              >
                {versionOptions.map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => handleAddColumn(value)}
                    className="block w-full text-center py-1.5 px-3 text-sm font-medium text-neu-primary hover:bg-blue-100 rounded-full transition-colors"
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Desktop View */}
          <div className="flex flex-col">
            <div className="hidden md:flex space-x-4">
              {columns.map((column, index) => (
                <div
                  key={`${column.version}-${column.book}-${column.chapter}-${index}`}
                  className="relative flex-1 bg-white p-4 rounded-[2rem] shadow-[-3px_-3px_8px_var(--outer-shadow-light),4px_4px_12px_var(--outer-shadow-dark)]"
                  style={{ flexBasis: `${100 / (columns.length + (isSermonEditorOpen ? 1 : 0))}%` }}
                >
                  <ColumnControls
                    column={column}
                    index={index}
                    handleVersionChange={(idx, value) => {
                      setColumns((prev) =>
                        prev.map((col, i) => {
                          if (i !== idx) return col;

                          let newBook = col.book;
                          if (isKoreanVersion(value) && !isKoreanVersion(col.version)) {
                            newBook = krvBookNames[col.book] || '창세기';
                          } else if (!isKoreanVersion(value) && isKoreanVersion(col.version)) {
                            newBook = Object.keys(koreanBookNames).find((key) => koreanBookNames[key] === col.book) || col.book;
                          }

                          return { ...col, version: value, book: newBook };
                        })
                      );
                    }}
                    handleBookChange={(idx, value) => {
                      setColumns((prev) =>
                        prev.map((col, i) => (i === idx ? { ...col, book: value } : col))
                      );
                    }}
                    handleChapterChange={(idx, value) => {
                      const sanitizedValue = String(value).replace(/[^0-9]/g, ''); // Ensure value is a string

                      // Allow the input to be cleared temporarily
                      if (sanitizedValue === '') {
                        setColumns((prev) =>
                          prev.map((col, i) => (i === idx ? { ...col, chapter: '' } : col))
                        );
                        return;
                      }

                      // Ensure the value is a positive number greater than zero
                      const chapter = Math.max(1, parseInt(sanitizedValue, 10) || 1);

                      setColumns((prev) =>
                        prev.map((col, i) => (i === idx ? { ...col, chapter } : col))
                      );
                    }}

                    handleMainSearch={handleMainSearch}
                    handleChapterNavigation={handleChapterNavigation}
                    removeColumn={removeColumn}
                    columnsLength={columns.length}
                  />
                  {/* Close Button */}
                  {columns.length > 1 && (
                    <button
                      onClick={() => removeColumn(column.id)}
                      className="text-red-500 hover:text-black bg-white rounded-full p-0 w-9 h-9 flex items-center justify-center font-semibold shadow-[-3px_-3px_6px_var(--outer-shadow-light),3px_3px_6px_var(--outer-shadow-dark)] hover:shadow-[-1px_-1px_3px_var(--outer-shadow-light),1px_1px_3px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_4px_var(--outer-shadow-dark),inset_-2px_-2px_4px_var(--outer-shadow-light)] transition-all closeBt"
                      style={{ zIndex: 10 }} // Ensure it appears on top
                    >
                      x
                    </button>
                  )}
                  {/* Verse Content */}
                  <div className="bible-verse-display">{renderVerses(column, index)}</div>
                  {/* Navigation Buttons */}
                  <div className="flex space-x-1 flex-[1_1_20%] justify-center">
                    <button
                      onClick={() => handleChapterNavigation("previous", index)}
                      className="text-black w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] hover:shadow-[-1px_-1px_3px_var(--outer-shadow-light),1px_1px_3px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_4px_var(--outer-shadow-dark),inset_-2px_-2px_4px_var(--outer-shadow-light)] transition-all"
                    >
                      <img src="/images/angle-left.png" alt="left" className='text-xs w-4 h-4 font-bold' />
                    </button>
                    <button
                      onClick={() => handleChapterNavigation("next", index)}
                      className="text-black w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] hover:shadow-[-1px_-1px_3px_var(--outer-shadow-light),1px_1px_3px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_4px_var(--outer-shadow-dark),inset_-2px_-2px_4px_var(--outer-shadow-light)] transition-all"
                    >
                      <img src="/images/angle-right.png" alt="right" className='text-xs w-4 h-4' />
                    </button>
                  </div>
                </div>
              ))}

              {/* Sermon Editor: Only for Desktop */}
              {isSermonEditorOpen && (
                <div
                  className="flex-1 bg-white p-4 rounded-[2rem] shadow-[-3px_-3px_8px_var(--outer-shadow-light),4px_4px_12px_var(--outer-shadow-dark)]"
                  style={{ flexBasis: `${100 / (columns.length + 1)}%` }}
                >
                  <SermonEditor
                    columns={columns}
                    sermon={currentSermon}
                    selectedVersion={selectedVersion}
                    selectedBook={selectedBook}
                    selectedChapter={selectedChapter}
                    selectedVerseId={selectedVerseId}
                    setIsSermonEditorOpen={setIsSermonEditorOpen}
                    setColumns={setColumns}
                    setSelectedTab={setSelectedTab}
                  />
                </div>
              )}
            </div>

            <div className="p-4 md:hidden m-container">
              <ul className="flex border-b mb-4">
                {columns.map((column, index) => (
                  <li
                    key={`${column.version}-${column.book}-${column.chapter}-${index}`}
                    className={`flex items-center justify-between flex-1 px-4 py-2 cursor-pointer ${selectedTab === index
                      ? "border-solid border-2 border-gray-200 text-blue-600 rounded-md bg-slate-200"
                      : "border-b text-gray-500 border-transparent hover:border-gray-300"
                      }`}
                    onClick={() => setSelectedTab(index)}
                  >
                    {/* Version Name */}
                    <span className="flex-grow text-center font-medium">{column.version}</span>

                    {/* Close Button */}
                    {columns.length > 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent triggering tab selection

                          setColumns((prevColumns) => {
                            // Remove the closed tab
                            const updatedColumns = prevColumns.filter((_, idx) => idx !== index);

                            // Save updated columns to localStorage
                            localStorage.setItem('columns', JSON.stringify(updatedColumns));

                            return updatedColumns;
                          });

                          setSelectedTab((prevSelectedTab) => {
                            // If the closed tab is the last one, move to the previous tab
                            if (index === prevSelectedTab && index === columns.length - 1) {
                              return Math.max(0, prevSelectedTab - 1);
                            }

                            // If the closed tab is not the selected one, retain the current tab
                            if (index !== prevSelectedTab) {
                              return prevSelectedTab;
                            }

                            // Otherwise, adjust for the removed tab
                            return Math.max(0, prevSelectedTab);
                          });
                        }}
                        className="hover:text-red-700 text-lg px-1"
                      >
                        x
                      </button>

                    )}
                  </li>
                ))}

                {/* Sermon Editor Tab */}
                {isSermonEditorOpen && (
                  <li
                    className={`flex items-center justify-center flex-1 px-4 py-2 cursor-pointer ${selectedTab === columns.length
                      ? "border-b-2 border-blue-500 text-blue-600"
                      : "border-b text-gray-500 border-transparent hover:border-gray-300"
                      }`}
                    onClick={() => setSelectedTab(columns.length)}
                  >
                    <span className="text-center font-medium">Sermon</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent tab selection
                        setIsSermonEditorOpen(false); // Close the sermon tab
                        // Set to the previously active tab or default to the first tab
                        setSelectedTab((prevSelectedTab) => (prevSelectedTab === columns.length ? prevSelectedTab - 1 : prevSelectedTab));
                      }}
                      className="hover:text-red-700 text-lg ml-2"
                    >
                      x
                    </button>
                  </li>
                )}
              </ul>

              {/* Tab Content */}
              {columns[selectedTab] && (
                <div className="bg-white p-4 rounded-[2rem] shadow-[-3px_-3px_8px_var(--outer-shadow-light),4px_4px_12px_var(--outer-shadow-dark)]">
                  <ColumnControls
                    column={columns[selectedTab]}
                    index={selectedTab}
                    handleVersionChange={(idx, newVersion) => {
                      setColumns((prevColumns) => {
                        return prevColumns.map((col, i) => {
                          if (i === idx) {
                            let newBook = col.book;

                            if (isKoreanVersion(newVersion) && !isKoreanVersion(col.version)) {
                              // Convert English to Korean
                              newBook = krvBookNames[col.book] || "창세기";
                            } else if (!isKoreanVersion(newVersion) && isKoreanVersion(col.version)) {
                              // Convert Korean to English
                              newBook =
                                Object.keys(koreanBookNames).find(
                                  (key) => koreanBookNames[key] === col.book
                                ) || col.book;
                            }

                            return { ...col, version: newVersion, book: newBook };
                          }
                          return col;
                        });
                      });
                    }}
                    
                    handleBookChange={(idx, value) => {
                      setColumns((prev) =>
                        prev.map((col, i) => (i === idx ? { ...col, book: value } : col))
                      );
                    }}
                    handleChapterChange={(idx, value) => {
                      const sanitizedValue = String(value).replace(/[^0-9]/g, ''); // Ensure value is a string

                      // Allow the input to be cleared temporarily
                      if (sanitizedValue === '') {
                        setColumns((prev) =>
                          prev.map((col, i) => (i === idx ? { ...col, chapter: '' } : col))
                        );
                        return;
                      }

                      // Ensure the value is a positive number greater than zero
                      const chapter = Math.max(1, parseInt(sanitizedValue, 10) || 1);

                      setColumns((prev) =>
                        prev.map((col, i) => (i === idx ? { ...col, chapter } : col))
                      );
                    }}
                    handleMainSearch={handleMainSearch}
                    handleChapterNavigation={handleChapterNavigation}
                    removeColumn={removeColumn}
                    columnsLength={columns.length}
                  />
                  <div className="bible-verse-display">{renderVerses(columns[selectedTab], selectedTab)}</div>
                  <div className="flex space-x-1 flex-[1_1_20%] justify-center">
                    <button
                      onClick={() => handleChapterNavigation("previous", selectedTab)}
                      className="text-black w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] hover:shadow-[-1px_-1px_3px_var(--outer-shadow-light),1px_1px_3px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_4px_var(--outer-shadow-dark),inset_-2px_-2px_4px_var(--outer-shadow-light)] transition-all"
                    >
                      <img src="/images/angle-left.png" alt="left" className='text-xs w-4 h-4' />
                    </button>
                    <button
                      onClick={() => handleChapterNavigation("next", selectedTab)}
                      className="text-black w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] hover:shadow-[-1px_-1px_3px_var(--outer-shadow-light),1px_1px_3px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_4px_var(--outer-shadow-dark),inset_-2px_-2px_4px_var(--outer-shadow-light)] transition-all "
                    >
                      <img src="/images/angle-right.png" alt="right" className='text-xs w-4 h-4  ' />
                    </button>
                  </div>
                </div>
              )}

              {/* Sermon Editor for Mobile View */}
              {isSermonEditorOpen && selectedTab === columns.length && (
                <div className="bg-white p-4 rounded-md shadow">
                  <SermonEditor
                    columns={columns}
                    sermon={currentSermon}
                    selectedVersion={selectedVersion}
                    selectedBook={selectedBook}
                    selectedChapter={selectedChapter}
                    selectedVerseId={selectedVerseId}
                    setIsSermonEditorOpen={setIsSermonEditorOpen}
                    setColumns={setColumns}
                    setSelectedTab={setSelectedTab}
                    selectedTab={selectedTab}
                    currentTabIndex
                  />
                </div>
              )}
            </div>

          </div>

          {/* Pop-up menu */}
          {isMenuOpen && (
            <div
              ref={menuRef}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              style={{ top: `${popupPosition.top}px`, left: `${popupPosition.left}px` }}
              className="absolute bg-white border border-gray-300 shadow-lg rounded-lg p-2 z-50 popup-debug"
            >
              {activeHighlightAction ? (
                <>
                  {showColorOptions ? (
                    <div className="block w-full py-1 px-3 text-sm rounded-md">
                      <div className="flex items-center justify-between mb-2 text-[11px] font-semibold uppercase text-gray-600">
                        <span className="pr-4">Change color</span>
                        <button
                          onClick={() => setShowColorOptions(false)}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          className="text-xs text-black-500 hover:text-gray-700 pl-2"
                        >
                          X
                        </button>
                      </div>
                      <div className="flex space-x-1">
                        {highlightColorOptions.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleHighlight(option.value);
                            }}
                            className="w-5 h-5 rounded-full border border-gray-200 shadow-sm hover:scale-105 transition-transform focus:outline-none"
                            style={{ backgroundColor: option.value }}
                            aria-label={`Highlight with ${option.label}`}
                            title={option.label}
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <>
                      <button
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openColorOptions();
                        }}
                        className="block w-full text-left py-1 px-3 text-sm hover:bg-yellow-100 rounded-md flex"
                      >
                        <HighlighterIcon className="h-4 w-4 mr-2" />
                        Change color
                      </button>
                      <button
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteHighlight(activeHighlightAction.highlight.id);
                        }}
                        className="block w-full text-left py-1 px-3 text-sm hover:bg-red-100 rounded-md flex text-red-700"
                      >
                        <DeleteHighlightIcon className="h-4 w-4 mr-2" />
                        Delete highlight
                      </button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <button
                    onClick={handleMakeNote}
                    className="block w-4/5 text-left py-1 px-3 text-sm hover:bg-blue-100 rounded-md flex"
                  >
                    <NotesIcon className="h-4 w-4 mr-2" />
                    {t('quickMenu.memos')}
                  </button>

                  {showColorOptions ? (
                    <div className="block w-full py-1 px-3 text-sm rounded-md">
                      <div className="flex items-center justify-between mb-2 text-[11px] font-semibold uppercase text-gray-600">
                        <span className="pr-4">Color options</span>
                        <button
                          onClick={() => setShowColorOptions(false)}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          className="text-xs text-black-500 hover:text-gray-700 pl-2"
                        >
                          X
                        </button>
                      </div>
                      <div className="flex space-x-1">
                        {highlightColorOptions.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log("[Highlight] Color swatch mousedown", option.value);
                              handleHighlight(option.value);
                            }}
                            className="w-5 h-5 rounded-full border border-gray-200 shadow-sm hover:scale-105 transition-transform focus:outline-none"
                            style={{ backgroundColor: option.value }}
                            aria-label={`Highlight with ${option.label}`}
                            title={option.label}
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <button
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openColorOptions();
                      }}
                      className="block w-full text-left py-1 px-3 text-sm hover:bg-yellow-100 rounded-md flex"
                    >
                      <HighlighterIcon className="h-4 w-4 mr-2" />
                      Highlights
                    </button>
                  )}

                  {verseHighlightAction && !showColorOptions && (
                    <>
                      <button
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setActiveHighlightAction(verseHighlightAction);
                          setVerseHighlightAction(null);
                          openColorOptions();
                        }}
                        className="block w-full text-left py-1 px-3 text-sm hover:bg-yellow-100 rounded-md flex"
                      >
                        <HighlighterIcon className="h-4 w-4 mr-2" />
                        Change highlight color
                      </button>
                      <button
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteHighlight(verseHighlightAction.highlight.id);
                        }}
                        className="block w-full text-left py-1 px-3 text-sm hover:bg-red-100 rounded-md flex text-red-700"
                      >
                        <DeleteHighlightIcon className="h-4 w-4 mr-2" />
                        Delete highlight
                      </button>
                    </>
                  )}

                  <button
                    onClick={handleSermon}
                    className="block w-4/5 text-left py-1 px-3 text-sm hover:bg-purple-100 rounded-md flex"
                  >
                    <img src='/images/speech.png' alt="Sermon Icon" className="h-4 w-4 mr-2" />
                    {t('quickMenu.sermon')}
                  </button>

                  <button
                    onClick={handleCopyVerse}
                    className="block w-full text-left py-1 px-3 text-sm hover:bg-green-100 rounded-md flex"
                  >
                    <CopyIcon className="h-4 w-4 mr-2" />
                    {t('quickMenu.copy')}
                  </button>
                </>
              )}
            </div>
          )}
          {/* Modal for Adding or Editing Notes */}
          {isModalOpen && (
            <NoteModal
              isOpen={isModalOpen}
              onClose={() => setModalOpen(false)}
              book={selectedBook}
              chapter={selectedChapter}
              verseNumber={selectedVerseId}
              note={currentNote}
              onSave={handleSaveNote}
              onDelete={handleDeleteNote}
            />
          )}

          {/* Highlight Modal */}
          {isHighlightModalOpen && (
            <HighlightModal
              isOpen={isHighlightModalOpen}
              highlights={highlightsForVerse}
              onClose={() => setIsHighlightModalOpen(false)}
              onDelete={handleDeleteHighlight}
              book={selectedBook}
              chapter={selectedChapter}
              verseNumber={selectedVerseId}
            />
          )}
          {/* Show custom alert if message exists */}
          {isAlertOpen && <CustomAlert message={alertMessage} onClose={closeAlert} autoDismissMs={alertAutoDismissMs} />}


        </>
      )}
    </div>
  );

};

export default BibleDisplay;
